import { useNavigate } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { WorkSpacesDocument } from 'generated/resources/graphql'
import { GetWorkAreaDocument } from 'generated/resources/graphql'
import { WorkAreaManagePolicyDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { trackingEvents } from 'modules/core/lib/Tracking/events'
import { track } from 'modules/core/lib/Tracking/tracker'
import i18n from 'modules/core/lib/i18n'

interface Props {
  workAreaId?: number
  selectedLocationId?: number
}

const EmptySpace = ({ workAreaId, selectedLocationId }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { data: workAreaData } = useQuery(GetWorkAreaDocument, {
    variables: { id: workAreaId ?? 0 },
    skip: !workAreaId,
  })

  const locationId = workAreaData?.locations?.workArea?.location?.id

  const { data: workSpacesData } = useQuery(WorkSpacesDocument, {
    variables: {
      locationIds: locationId ? [locationId] : [],
      workAreaIds: workAreaId ? [workAreaId] : [],
    },
    skip: !workAreaId || !locationId,
  })

  const { data: workAreaManageData } = useQuery(WorkAreaManagePolicyDocument)

  const canAddSpace = workSpacesData?.spaceControl._policies.workSpaceCreate.can ?? false
  const canManageWorkArea = workAreaManageData?.locations?._policies?.workAreaManage?.can ?? false

  const addSpaceClick = () => {
    if (!locationId || !workAreaId) return

    track(trackingEvents.spaceControl.addSpace, {})
    navigate(
      navigation.settings.locations.locationsId.workAreas.workSpaces.addWorkSpace.fullPath({
        id: locationId,
        workAreaId: workAreaId,
      })
    )
  }

  const createAction = (onClick: () => void) => ({
    label: i18n.t('space_control.overview.go_to_settings'),
    onClick,
    type: 'default' as const,
    variant: 'outline' as const,
  })

  const getActions = () => {
    if (canAddSpace && locationId && workAreaId) {
      return [createAction(addSpaceClick)]
    }

    if (canManageWorkArea) {
      if (selectedLocationId) {
        return [
          createAction(() =>
            navigate(
              navigation.settings.locations.locationsId.workAreas.fullPath({
                id: selectedLocationId,
              })
            )
          ),
        ]
      }
      return [createAction(() => navigate(navigation.settings.locations.fullPath()))]
    }

    return []
  }

  return (
    <Box height="s400" flexGrow alignItems="center" justifyContent="center" gap="s16">
      <OneEmptyState
        title={i18n.t('space_control.overview.no_spaces_found')}
        description={i18n.t('space_control.overview.create_spaces')}
        emoji={'🏢'}
        actions={getActions()}
      />
    </Box>
  )
}

export default observer(EmptySpace)
